import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';

import type { Assessment } from '../domain/scoring';
import type { Stufe } from '../platform/appLog';
import type { PersistenceKey } from '../ui/toastText';

/**
 * Sichern des laufenden Bogens.
 *
 * Gedämpft nach 500 ms, damit nicht jedes Häkchen eine Schreibrunde auslöst. Sofort und ohne
 * Dämpfung beim Wechsel in den Hintergrund (`App`-Ereignis `appStateChange`, `isActive ===
 * false` — spec-vorgeschrieben, feuert bei `willResignActive` und damit früher als `pause`
 * [`didEnterBackground`]; ein Sichern zu viel ist folgenlos, weil gedämpft wird und nur
 * Unterschiede geschrieben werden, ein Sichern zu spät kostet die Bewertung), bei `pagehide`/
 * `visibilitychange` und beim Abgeben — die beiden Fensterereignisse fangen den abgeräumten
 * WKWebView-Inhaltsprozess ab, der ohne App-Ereignis daherkommt.
 *
 * Die Abhängigkeiten kommen von außen, damit der Ablauf ohne React und ohne Gerät prüfbar ist.
 */

export const SAVE_DELAY_MS = 500;

/**
 * Wessen Bewertung `read()` liefert — Fix-Runde zu Aufgabe 17: ein Ausweisscan mitten in der
 * Dämpfung ändert `candidateId` per `set()`; dasselbe `set()` löst über `subscribe(plane)` den
 * Dämpfer erneut aus, der alte Zeitgeber verfällt, ein neuer beginnt. Feuert der, lieferte
 * `read()` (als reines `() => Assessment`) den *dann* aktuellen Prüfling, während der zuvor
 * gemerkte Stand (`zuletzt`) noch zum vorherigen gehörte — Bewertung und Bezug kamen aus zwei
 * verschiedenen Momenten. Der Bezug wurde zudem ein zweites Mal, unabhängig, erst in `save()`
 * aus dem *dann* aktuellen Store gebaut — ein zweites Fenster für denselben Fehler. Deckungsgleich
 * mit `persistence/repos.ts::LogContext`, hier aber eigenständig definiert: dieses Modul bleibt
 * unabhängig von der Persistenzschicht (s. Kommentar oben), damit es ohne sie testbar bleibt.
 */
export interface AssessmentContext {
  examID: string;
  candidateID: string;
  examinerID: string | null;
  actorID: string | null;
}

/**
 * Bewertung und ihr Bezug aus **einem** `read()`-Aufruf — atomar, nicht als zwei getrennte
 * Zugriffe zu zwei verschiedenen Zeitpunkten. Genau das war die Lücke: lag zwischen dem
 * Anstoßen einer Sicherung und ihrem Feuern ein Prüflingswechsel, gehörte die Bewertung noch
 * zum alten Prüfling, der separat aufgelöste Bezug aber schon zum neuen — ihr Unterschied
 * landete unter der falschen Kennung im Protokoll.
 */
export interface AssessmentSnapshot {
  ctx: AssessmentContext;
  assessment: Assessment;
  /**
   * Die Station, an der gerade bewertet wird — Ziel des Stationskommentars (seit 2026-09-13).
   * Sie gehört in denselben Schnappschuss wie die Bewertung: nachträglich aus dem Store
   * geholt könnte sie zu einer anderen Station gehören als der Stand, der geschrieben wird.
   */
  stationId?: string;
}

export interface AutosaveDeps {
  /** Der aktuelle Bogen des gewählten Prüflings, mitsamt seinem Bezug — s. `AssessmentSnapshot`. */
  read: () => AssessmentSnapshot;
  /**
   * Schreibt die Unterschiede zwischen zwei Ständen; Rückgabe ist die Zahl der Zeilen. `vorher`
   * und `nachher` gehören laut der Wache in `schreibe()` unten immer zum selben Prüfling — wer
   * hier eigenmächtig noch einmal Kontext aus einer anderen Quelle nachlädt (etwa einem frisch
   * gerufenen `getState()`), öffnet genau das Fenster wieder, das diese Wache schließt.
   */
  save: (vorher: AssessmentSnapshot, nachher: AssessmentSnapshot) => Promise<number>;
  /**
   * Das Dateilog. `stufe` steuert, auf welcher Konsolenfunktion die Zeile am Kabel herauskommt
   * (s. `platform/appLog.ts`) — in der Datei sieht jede Zeile gleich aus. Aufzurufen
   * ausschliesslich über `protokolliere()` unten, nie direkt.
   */
  log: (text: string, stufe?: Stufe) => Promise<void>;
  /**
   * Sprachschlüssel unter `t.persistence` — nie fertiger Text (s. `Toast.tsx`). Aufzurufen
   * ausschließlich über `melde(deps, …)` unten, nie direkt: wer hier direkt schreibt, öffnet
   * dasselbe Fenster wieder, das `melde()` schließt.
   */
  toast: (key: PersistenceKey) => void;
  /** Meldet Änderungen an; gibt die Abmeldung zurück. */
  subscribe: (fn: () => void) => () => void;
}

// Modulweiter Zustand, bewusst ein einziger Satz statt einer Instanz je Aufruf: `läuft` muss
// über den Wechsel von einer Sitzung (Prüfling A) zur nächsten (Prüfling B) hinweg dieselbe
// Kette bleiben, sonst könnten zwei `schreibe()`-Aufrufe gleichzeitig in `store.tx()` laufen —
// `tx()` ist laut Vorgabe nicht verschachtelbar. Eine neue Sitzung reiht sich deshalb hinten in
// dieselbe Kette ein, statt eine eigene zu eröffnen.
let geplant: ReturnType<typeof setTimeout> | null = null;
let läuft: Promise<void> | null = null;
let zuletzt: AssessmentSnapshot | null = null;
let aktiv: AutosaveDeps | null = null;

/**
 * Dritte Fix-Runde zu Aufgabe 17: `deps.log(...)` stand an allen Stellen in `schreibe()` ohne
 * `try`/`catch` da. Wirft `log()` — etwa weil die Log-Implementierung ausgetauscht wird und die
 * neue anders scheitert als die heutige (die laut eigenem Kommentar in `platform/appLog.ts` nie
 * werfen darf, aber das ist eine Zusicherung in einer fremden Datei, keine Verteidigung hier) —,
 * reißt das `schreibe(deps)` selbst zum Scheitern. `läuft = (läuft ?? Promise.resolve()).then(()
 * => schreibe(deps))` macht daraus eine **dauerhaft** `rejected` Kette: jeder künftige
 * `.then(() => schreibe(deps))` auf einer bereits abgelehnten Kette ruft seinen Rückruf nie
 * wieder auf, weder über `plane()` noch über `flushNow()` — und `läuft` wird beim Abmelden nicht
 * zurückgesetzt. Ein einziger werfender Log-Aufruf würde die Sicherung für den Rest der
 * Modul-Lebensdauer lautlos stilllegen.
 *
 * Eine gemeinsame Hülle statt sechs einzelner `try`/`catch`-Blöcke: sie sichert nicht nur die
 * sechs heutigen Aufrufstellen ab, sondern auch jede künftige, die hier noch dazukommt — wer
 * `deps.log(...)` direkt schreibt statt `protokolliere(deps, ...)`, öffnet das Fenster wieder.
 */
async function protokolliere(deps: AutosaveDeps, text: string, stufe: Stufe = 'info'): Promise<void> {
  try {
    await deps.log(text, stufe);
  } catch {
    // Das Protokoll selbst darf nicht zum Ausfall der Sicherung führen. Mehr lässt sich hier
    // nicht tun, ohne wiederum von einem (möglicherweise ebenso kaputten) Protokoll abzuhängen.
  }
}

/**
 * Schlusspruefung des Zweigs (Ruling P50): dieselbe Hülle für `deps.toast(...)`. Der Aufruf
 * stand als einziger ungeschützt da — mit der Begründung, er sei synchron. Das trägt nicht:
 * er steht zwar in einem `catch`, aber ein Wurf **innerhalb** eines `catch` verlässt die
 * Funktion genauso, und eine `async`-Funktion lehnt auch bei einem synchronen Wurf ab. Die
 * `läuft`-Kette bliebe damit dauerhaft `rejected` — exakt der Ausfall, den `protokolliere()`
 * für die Log-Aufrufe verhindert, nur an der einen Stelle, die ausgerechnet dann feuert, wenn
 * ohnehin schon zweimal nicht geschrieben werden konnte.
 *
 * Sie ist nicht `async`: die Einblendung ist ein Zustand-Setzer und liefert nichts, worauf zu
 * warten wäre. Ein `await` hier ergäbe nur eine zusätzliche Mikroaufgabe.
 */
function melde(deps: AutosaveDeps, key: PersistenceKey): void {
  try {
    deps.toast(key);
  } catch {
    // Wie beim Protokoll: eine gescheiterte Einblendung darf die Sicherung nicht stilllegen.
    // Ein Protokolleintrag darüber wäre hier zirkulär — `protokolliere()` kann seinerseits
    // scheitern, und der Anlass ist ja gerade, dass schon nichts mehr geschrieben werden kann.
  }
}

/**
 * Ein Schreibversuch. Prüft vor **jeder** Nebenwirkung (Zustandsänderung, Einblendung), ob
 * `deps` noch die aktive Sitzung ist — wurde inzwischen abgemeldet oder eine neue Sitzung
 * gestartet (`aktiv` zeigt auf andere `deps`), bricht der Versuch stumm ab. Ohne diese Wache
 * könnte ein Aufruf, der noch aus der alten Sitzung in der `läuft`-Kette wartete, nach dem
 * Prüflingswechsel den Stand der neuen Sitzung mit den (veralteten) Daten der alten
 * überschreiben oder eine Einblendung für einen Prüfling zeigen, der gar nicht mehr auf dem
 * Bildschirm ist.
 *
 * ⚠️ **Zusicherung seit der Fix-Runde zu Aufgabe 17:** ein Unterschied wird nie geschrieben,
 * wenn `vorher` und `nachher` zu verschiedenen Prüflingen gehören. Das ist die zweite,
 * unabhängige Sicherung neben der Sitzungswache oben — jene schützt vor einer *anderen*
 * `startAutosave`-Sitzung, diese hier vor einem Prüflingswechsel *innerhalb* derselben Sitzung
 * (der Regelfall: `main.tsx` startet `startAutosave` genau einmal für die ganze Laufzeit der
 * App, jeder Ausweisscan bleibt dieselbe Sitzung).
 *
 * ⚠️ **Zweite Fix-Runde zu Aufgabe 17: diese Wache ist die zweite Verteidigungslinie, nicht
 * die erste.** Die erste ist `resetBaseline()` — jeder Pfad, der den Prüfling wechselt, ruft
 * sie unmittelbar nach dem Wechsel auf und setzt `zuletzt` dadurch selbst auf den frischen
 * Prüfling um, *bevor* eine Eingabe an ihm überhaupt möglich ist. Käme diese Wache hier zum
 * Zug, träfe sie nicht nur den (harmlosen) Wechsel selbst, sondern auch die *erste, echte*
 * Eingabe am neuen Prüfling im selben Dämpfer-Zyklus — die ginge dann als „schon immer so
 * gewesen" unter, ohne je geschrieben zu werden. Mit `resetBaseline()` an jedem Wechselpfad
 * ist dieser Zweig im Normalbetrieb **unerreichbar**; greift er trotzdem, hat irgendein Pfad
 * den Prüfling gewechselt, ohne zurückzusetzen — das gehört ins Dateilog, nicht in ein
 * stilles Verwerfen.
 */
async function schreibe(deps: AutosaveDeps): Promise<void> {
  if (deps !== aktiv) return;

  // `read()` ist die einzige Wurfquelle dieser Funktion ausserhalb eines `try`. Es liest den
  // Store und sollte nicht werfen — aber genau dasselbe galt für die Einblendung, bis sie es
  // tat. Wirft es doch, gilt derselbe Grundsatz wie überall hier: aufschreiben und diesen einen
  // Versuch aufgeben, statt die Kette stillzulegen.
  let nachher: AssessmentSnapshot;
  try {
    nachher = deps.read();
  } catch (fehler) {
    await protokolliere(deps, 'Lesen des Standes fehlgeschlagen: ' + String(fehler), 'error');
    return;
  }

  // ⚠️ **Ohne gewählten Prüfling wird nicht geschrieben.** Seit dem Startablauf
  // (`store/startup.ts`) ist „noch niemand gewählt" ein echter Zustand: zwischen dem Öffnen
  // der App und dem Ausweisscan steht der Bogen bereits da, aber `candidateID` ist leer.
  // `ExamLog.candidateID` ist nullable — die Zeilen entstünden also klaglos und wären
  // Datenmüll in der Auswertung, ohne dass irgendetwas fehlschlüge. Der Ausgangsstand wird
  // trotzdem nachgezogen: sonst gälte der leere Stand später als Unterschied und der erste
  // echte Eintrag des dann gewählten Prüflings verglichen sich gegen ihn.
  if (!nachher.ctx.candidateID) {
    // Aufgeschrieben und nicht still verworfen — dieselbe Regel wie bei der Prüflings-Wache
    // unten. Im Regelfall (Startablauf, vor dem Ausweisscan) ist es harmlos; träfe es je auf
    // einen echten ausstehenden Unterschied, wäre das eine Lücke, die man sehen können muss.
    await protokolliere(
      deps,
      'Sicherung übersprungen: kein Prüfling gewählt. Der Stand gilt ab jetzt als Ausgangspunkt.',
    );
    if (deps === aktiv) zuletzt = nachher;
    return;
  }

  if (zuletzt !== null && zuletzt.ctx.candidateID !== nachher.ctx.candidateID) {
    await protokolliere(
      deps,
      'Prüflings-Wache in schreibe() ausgelöst (' +
        zuletzt.ctx.candidateID +
        ' -> ' +
        nachher.ctx.candidateID +
        ') — resetBaseline() hätte das nach dem Wechsel verhindern müssen. Der Unterschied ' +
        'wurde nicht geschrieben; der neue Stand gilt ab jetzt als Ausgangspunkt.',
      'warn',
    );
    if (deps === aktiv) zuletzt = nachher;
    return;
  }

  const vorher = zuletzt;
  if (vorher !== null && vorher.assessment === nachher.assessment) return;

  await protokolliere(deps, 'Sicherung begonnen');
  try {
    await deps.save(vorher ?? nachher, nachher);
    if (deps === aktiv) zuletzt = nachher;
    return;
  } catch (fehler) {
    // `warn`, nicht `error`: gleich darauf folgt die Wiederholung, und erst wenn die auch
    // scheitert, ist etwas endgültig nicht geschrieben.
    await protokolliere(deps, 'Sicherung fehlgeschlagen: ' + String(fehler), 'warn');
  }

  if (deps !== aktiv) return;

  // Einmal wiederholen, sofort und ohne weitere Dämpfung. Scheitert auch das, geht es an den
  // Prüfer — er muss erfahren, dass seine Eingaben nicht sicher liegen.
  await protokolliere(deps, 'Wiederholung der Sicherung');
  try {
    await deps.save(zuletzt ?? nachher, nachher);
    if (deps === aktiv) zuletzt = nachher;
    await protokolliere(deps, 'Wiederholung erfolgreich');
  } catch (fehler) {
    // ⬅️ **Die Zeile, auf die es ankommt.** Genau hier ist etwas endgültig nicht geschrieben,
    // und genau hier hängt die Einblendung darunter. In Xcode steht sie als Fehler.
    await protokolliere(deps, 'Wiederholung fehlgeschlagen: ' + String(fehler), 'error');
    // `zuletzt` bleibt stehen: die nächste Sicherung holt alles seit dem letzten geglückten
    // Schreiben nach. Der Speicher bleibt so lange die Wahrheit. Dieselbe Meldung erscheint bei
    // jedem weiteren Fehlschlag erneut — die Einblendung verdrängt sich selbst (s. `Toast.tsx`),
    // das hält die Warnung sichtbar, solange das Problem besteht, statt nach 4 Sekunden
    // stillschweigend zu verschwinden.
    if (deps === aktiv) melde(deps, 'writeFailed');
  }
}

/** Sofort sichern, ohne Dämpfung. Läuft schon eine Sicherung, reiht sich diese dahinter ein. */
export async function flushNow(): Promise<void> {
  if (!aktiv) return;
  if (geplant) {
    clearTimeout(geplant);
    geplant = null;
  }
  const deps = aktiv;
  // Zwei Zusagen aus einer: `versuch` behält eine etwaige Ablehnung, damit der Aufrufer von
  // einem Fehlschlag erfährt; `läuft` bekommt die entschärfte Fassung, damit eine Ablehnung
  // die Kette nicht dauerhaft stilllegt (siehe die Erklärung im Dämpfer-Pfad).
  const versuch = (läuft ?? Promise.resolve()).then(() => schreibe(deps));
  läuft = versuch.catch(() => undefined);
  await versuch;
}

/**
 * Nach **jedem** Prüflingswechsel aufzurufen — synchron, unmittelbar nachdem `candidateId`
 * gewechselt hat (also direkt nach dem `set()`, das ihn ändert) und bevor eine Eingabe am
 * neuen Prüfling überhaupt möglich ist. Setzt den Ausgangsstand des Autospeicherns auf den
 * *jetzt* geladenen Stand des neuen Prüflings zurück, statt ihn stehen zu lassen.
 *
 * ⚠️ **Ohne diesen Aufruf träfe der nächste Dämpfer-Zyklus unweigerlich die Prüflings-Wache
 * in `schreibe()`.** Das ist nicht bloß eine verspätete, sondern eine **verlorene** Sicherung:
 * die Wache verwirft dabei nicht nur den (zu Recht bedeutungslosen) Unterschied zwischen altem
 * und neuem Prüfling, sondern jede *echte* Eingabe, die der Prüfer in genau diesem ersten
 * Dämpfer-Fenster am neuen Prüfling macht — sie würde ununterscheidbar zum „schon immer so
 * gewesen" des zurückgesetzten Standes und nie geschrieben. Der plausible Fall: Ausweis
 * scannen, sofort das erste Häkchen setzen.
 *
 * Reine Synchronität ist hier die Zusicherung, kein Zufall: `read()` liest den Store direkt
 * (kein `await` dazwischen), und der Aufrufer ruft diese Funktion seinerseits synchron im
 * selben Aufruf wie das wechselnde `set()` — kein Timer und keine Store-Änderung kann
 * zwischen beiden ausgeführt werden, weil JavaScript einen synchronen Ablauf nie unterbricht.
 *
 * Kein Aufruf, wenn keine Sitzung läuft (`aktiv === null`) — dann gibt es nichts
 * zurückzusetzen; `startAutosave()` selbst setzt beim Start ohnehin `zuletzt = deps.read()`.
 */
export function resetBaseline(): void {
  if (aktiv) zuletzt = aktiv.read();
}

export function startAutosave(deps: AutosaveDeps): () => void {
  aktiv = deps;
  zuletzt = deps.read();

  const plane = () => {
    if (geplant) clearTimeout(geplant);
    geplant = setTimeout(() => {
      geplant = null;
      // Das abschliessende `catch` ist die Absicherung gegen genau die Fehlerklasse, die P50
      // aufgedeckt hat: bleibt `läuft` abgelehnt, überspringt jedes spätere `.then()` seine
      // Rückgabe, und es liefe nie wieder eine Sicherung — für die Lebensdauer des Moduls.
      // `schreibe()` ist so gebaut, dass es nicht ablehnt; dies fängt ab, wenn das doch einmal
      // nicht mehr stimmt.
      läuft = (läuft ?? Promise.resolve()).then(() => schreibe(deps)).catch(() => undefined);
    }, SAVE_DELAY_MS);
  };

  const abmeldenStore = deps.subscribe(plane);

  const sofort = () => {
    void flushNow();
  };

  // `App.addListener` lädt auf der Web-Plattform beim ersten Aufruf seine Web-Implementierung
  // nach, die im Konstruktor `document.addEventListener` ruft — im Node-Testlauf (kein DOM) gäbe
  // das einen Fehler. Dieselbe Bedingung wie unten für die Fensterereignisse: ohne `window` gibt
  // es auch kein sinnvolles „in den Hintergrund", also wird dort gar nicht erst angemeldet.
  const amFenster = typeof window !== 'undefined';

  if (amFenster) {
    window.addEventListener('pagehide', sofort);
    window.addEventListener('visibilitychange', sofort);
  }

  // Fängt den Wechsel in den Hintergrund auf dem Gerät ab — spec-vorgeschrieben `appStateChange`
  // mit `isActive === false`, nicht `pause`: `appStateChange` feuert bei `willResignActive`,
  // also auch bei einer kurzen Unterbrechung (z. B. Kontrollzentrum), und damit früher als
  // `pause` (`didEnterBackground`). `addListener` ist asynchron; die Abmeldung unten wartet auf
  // dasselbe Versprechen, statt eine noch unbekannte Kennung vorwegzunehmen — so bleibt sie auch
  // dann sauber, wenn `startAutosave` und die Abmeldung sehr schnell aufeinanderfolgen.
  const appAbmeldung = amFenster
    ? App.addListener('appStateChange', (state) => {
        if (!state.isActive) sofort();
      }).catch(() => null)
    : Promise.resolve(null);

  return () => {
    abmeldenStore();
    if (amFenster) {
      window.removeEventListener('pagehide', sofort);
      window.removeEventListener('visibilitychange', sofort);
    }
    void appAbmeldung.then((handle: PluginListenerHandle | null) => handle?.remove());
    if (geplant) clearTimeout(geplant);
    geplant = null;
    aktiv = null;
    zuletzt = null;
    // Auch die Kette: sonst überdauerte ein Fehlzustand die Sitzung, in der er entstand.
    läuft = null;
  };
}
